import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Soft badge tones (`.b-*`). `live` (alias `info`) is the action tint -- answering,
 * selected, informational; `ok` positive; `warn` needs a look; `acc` (Ember) costs
 * money or a customer if ignored, rationed; `neutral` everything else.
 */
export type BadgeTone = 'live' | 'info' | 'ok' | 'warn' | 'acc' | 'neutral'

interface BadgeProps extends ComponentProps<'span'> {
  tone?: BadgeTone
  /** A leading status dot; `pulse` animates it. Always paired with text, never alone. */
  dot?: boolean | 'pulse'
}

/** A fact: an outcome, a status. Plain badge, never dashed (that is the estimate chip). */
export function Badge({ tone = 'neutral', dot, className, children, ...props }: BadgeProps) {
  const toneClass = `b-${tone === 'info' ? 'live' : tone}`
  return (
    <span className={cn('badge', toneClass, className)} {...props}>
      {dot ? <span className={cn('dot', dot === 'pulse' && 'pulse')} aria-hidden="true" /> : null}
      {children}
    </span>
  )
}

/**
 * The dashed "≈" chip (`.badge.b-est`): an AI estimate, never a fact. Sentiment,
 * urgency and anything inferred carry it, and it never triggers automatic
 * treatment (design docs §01). The "≈" is drawn by CSS.
 */
export function EstimateChip({ className, children, ...props }: ComponentProps<'span'> & { children: ReactNode }) {
  return (
    <span className={cn('badge b-est', className)} {...props}>
      {children}
    </span>
  )
}
